import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Unlock, Trash2, Loader2, BadgeEuro, Tag, Search, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_LABELS = {
  standard: 'Standard',
  bridal: 'Sposa',
  elite: 'Elite'
};

const CATEGORY_COLORS = {
  standard: 'bg-gray-100 text-gray-700',
  bridal: 'bg-pink-100 text-pink-700',
  elite: 'bg-amber-100 text-amber-700'
};

const AdminEccezioniPage = () => {
  const [overrides, setOverrides] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [productOverrides, setProductOverrides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newOrder, setNewOrder] = useState('');
  const [newReason, setNewReason] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newWaiverOrder, setNewWaiverOrder] = useState('');
  const [newWaiverReason, setNewWaiverReason] = useState('');
  const [isAddingWaiver, setIsAddingWaiver] = useState(false);
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('standard');
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    const token = localStorage.getItem('admin_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [ovRes, wvRes, poRes] = await Promise.all([
        fetch(`${API_BASE}/admin/order-overrides`, { headers }),
        fetch(`${API_BASE}/admin/cost-waivers`, { headers }),
        fetch(`${API_BASE}/admin/product-category-overrides`, { headers })
      ]);
      if (ovRes.ok) setOverrides(await ovRes.json());
      if (wvRes.ok) setWaivers(await wvRes.json());
      if (poRes.ok) setProductOverrides(await poRes.json());
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  };

  // ---- Load all products from catalog ----
  const loadProducts = async () => {
    setIsLoadingProducts(true);
    setShowSearch(true);
    try {
      const res = await fetch(`${API_BASE}/orders/products`);
      if (res.ok) {
        const data = await res.json();
        setAllProducts(data || []);
      }
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento catalogo');
    }
    setIsLoadingProducts(false);
  };

  // Filter products client-side
  const existingIds = new Set(productOverrides.map(o => String(o.product_id)));
  const filteredProducts = allProducts
    .filter(p => !existingIds.has(String(p.id)))
    .filter(p => {
      if (!productSearch) return true;
      const q = productSearch.toLowerCase();
      return (p.title || '').toLowerCase().includes(q) || (p.product_type || '').toLowerCase().includes(q);
    })
    .slice(0, 20);

  const handleAddProductOverride = async (product) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/product-category-overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          product_id: String(product.id),
          product_title: product.title,
          product_image: product.image?.src || product.images?.[0]?.src || '',
          product_type: product.product_type || '',
          force_category: selectedCategory
        })
      });
      if (res.ok) {
        toast.success(`"${product.title}" impostato come ${CATEGORY_LABELS[selectedCategory]}`);
        setProductSearch('');
        setShowSearch(false);
        fetchAll();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore di connessione'); }
  };

  const handleDeleteProductOverride = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/product-category-overrides/${encodeURIComponent(productId)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Eccezione rimossa'); fetchAll(); }
      else toast.error('Errore nella rimozione');
    } catch (e) { toast.error('Errore di connessione'); }
  };

  // ---- Order Overrides ----
  const handleAdd = async () => {
    if (!newOrder.trim()) { toast.error('Inserisci il numero ordine'); return; }
    setIsAdding(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/order-overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order_number: newOrder.trim(), reason: newReason.trim() || 'Sbloccato dall\'amministratore' })
      });
      if (res.ok) {
        toast.success(`Ordine ${newOrder.trim()} sbloccato`);
        setNewOrder(''); setNewReason(''); fetchAll();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore di connessione'); }
    setIsAdding(false);
  };

  const handleDelete = async (orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/order-overrides/${encodeURIComponent(orderNumber)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success(`Sblocco per ${orderNumber} rimosso`); fetchAll(); }
      else toast.error('Errore nella rimozione');
    } catch (e) { toast.error('Errore di connessione'); }
  };

  // ---- Cost Waivers ----
  const handleAddWaiver = async () => {
    if (!newWaiverOrder.trim()) { toast.error('Inserisci il numero ordine'); return; }
    setIsAddingWaiver(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/cost-waivers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order_number: newWaiverOrder.trim(), reason: newWaiverReason.trim() || 'Costi azzerati dall\'amministratore' })
      });
      if (res.ok) {
        toast.success(`Costi azzerati per ${newWaiverOrder.trim()}`);
        setNewWaiverOrder(''); setNewWaiverReason(''); fetchAll();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore di connessione'); }
    setIsAddingWaiver(false);
  };

  const handleDeleteWaiver = async (orderNumber) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/cost-waivers/${encodeURIComponent(orderNumber)}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success(`Costi ripristinati per ${orderNumber}`); fetchAll(); }
      else toast.error('Errore nella rimozione');
    } catch (e) { toast.error('Errore di connessione'); }
  };

  const renderList = (items, type) => {
    const isWaiver = type === 'waiver';
    const EmptyIcon = isWaiver ? BadgeEuro : ShieldCheck;
    const itemColor = isWaiver ? 'emerald' : 'purple';
    const emptyText = isWaiver ? 'Nessun ordine con costi azzerati' : 'Nessun ordine sbloccato';
    const emptySubtext = isWaiver ? 'Gli ordini con costi azzerati appariranno qui' : 'Gli ordini sbloccati appariranno qui';
    const deleteFn = isWaiver ? handleDeleteWaiver : handleDelete;

    if (isLoading) {
      return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
    }
    if (items.length === 0) {
      return (
        <div className="text-center py-12 text-gray-400">
          <EmptyIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{emptyText}</p>
          <p className="text-xs mt-1">{emptySubtext}</p>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {items.map((o) => (
          <div key={o.order_number} className={`flex items-center justify-between p-4 bg-${itemColor}-50 border border-${itemColor}-100 rounded-lg hover:bg-${itemColor}-100 transition-colors`} data-testid={`${type}-item-${o.order_number}`}>
            <div className="flex items-center gap-3">
              {isWaiver ? <BadgeEuro className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <ShieldCheck className="w-5 h-5 text-purple-600 flex-shrink-0" />}
              <div>
                <p className="text-sm font-semibold text-gray-900">{o.order_number}</p>
                <p className="text-xs text-gray-500">
                  {o.reason}
                  {o.created_at && <span className="ml-2 text-gray-400">{new Date(o.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                </p>
              </div>
            </div>
            <button onClick={() => deleteFn(o.order_number)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title={isWaiver ? 'Ripristina costi' : 'Rimuovi sblocco'} data-testid={`${type}-delete-${o.order_number}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-eccezioni-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Eccezioni</h1>
        <p className="text-sm text-gray-500 mt-1">
          Gestisci eccezioni su ordini e categorie prodotti.
        </p>
      </div>

      {/* ============ ECCEZIONE CATEGORIA PRODOTTI ============ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-orange-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Tag className="w-5 h-5 text-orange-600" />
          Eccezione Categoria Prodotto
        </h3>
        <p className="text-xs text-gray-400 mb-4">Forza la categoria di un prodotto per il calcolo dei costi. Il sistema usa l'ID prodotto Shopify per il match.</p>
        
        {!showSearch ? (
          <Button onClick={loadProducts} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="product-override-add-btn">
            {isLoadingProducts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Cerca prodotto da aggiungere
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filtra per nome prodotto..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  autoFocus
                  data-testid="product-search-input"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="product-category-select"
              >
                <option value="standard">Standard (costi normali)</option>
                <option value="bridal">Sposa (costi maggiorati)</option>
                <option value="elite">Elite (costi elite)</option>
              </select>
              <button onClick={() => { setShowSearch(false); setProductSearch(''); setAllProducts([]); }} className="p-2.5 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {isLoadingProducts ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-400" /><p className="text-xs text-gray-400 mt-2">Caricamento catalogo...</p></div>
            ) : filteredProducts.length > 0 ? (
              <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleAddProductOverride(product)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-orange-50 transition-colors text-left"
                    data-testid={`product-result-${product.id}`}
                  >
                    {(product.image?.src || product.images?.[0]?.src) ? (
                      <img src={product.image?.src || product.images?.[0]?.src} alt="" className="w-10 h-10 object-cover rounded-md border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center"><Package className="w-5 h-5 text-gray-400" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                      <p className="text-xs text-gray-400">{product.product_type || 'Nessun tipo'} &middot; ID: {product.id}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[selectedCategory]}`}>
                      {CATEGORY_LABELS[selectedCategory]}
                    </span>
                  </button>
                ))}
              </div>
            ) : allProducts.length > 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nessun prodotto trovato{productSearch ? ` per "${productSearch}"` : ''}</p>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">Nessun prodotto nel catalogo</p>
            )}
          </div>
        )}
      </motion.div>

      {/* Lista eccezioni prodotti */}
      {productOverrides.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.02 }} className="bg-white rounded-xl border border-orange-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5 text-orange-600" />
            Prodotti con categoria forzata
            <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">{productOverrides.length}</span>
          </h3>
          <div className="space-y-2">
            {productOverrides.map((o) => (
              <div key={o.product_id} className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-lg" data-testid={`product-override-${o.product_id}`}>
                <div className="flex items-center gap-3">
                  {o.product_image ? (
                    <img src={o.product_image} alt="" className="w-10 h-10 object-cover rounded-md border border-gray-200" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center"><Package className="w-5 h-5 text-gray-400" /></div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{o.product_title}</p>
                    <p className="text-xs text-gray-400">
                      {o.product_type || 'Nessun tipo'} &middot; ID: {o.product_id}
                      {o.created_at && <span className="ml-2">{new Date(o.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${CATEGORY_COLORS[o.force_category] || CATEGORY_COLORS.standard}`}>
                    {CATEGORY_LABELS[o.force_category] || o.force_category}
                  </span>
                  <button onClick={() => handleDeleteProductOverride(o.product_id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Rimuovi eccezione" data-testid={`product-override-delete-${o.product_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* ============ SBLOCCA ORDINE ============ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Unlock className="w-5 h-5 text-purple-600" />
          Sblocca un ordine
        </h3>
        <p className="text-xs text-gray-400 mb-3">Consenti richieste oltre la finestra dei 14 giorni dalla consegna.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input type="text" placeholder="Numero ordine (es: DForder_21938)" value={newOrder} onChange={(e) => setNewOrder(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdd()} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="eccezioni-order-input" />
          </div>
          <div className="flex-1">
            <input type="text" placeholder="Motivo (opzionale)" value={newReason} onChange={(e) => setNewReason(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdd()} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="eccezioni-reason-input" />
          </div>
          <Button onClick={handleAdd} disabled={isAdding || !newOrder.trim()} className="bg-purple-600 hover:bg-purple-700 text-white px-6" data-testid="eccezioni-add-btn">
            {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Unlock className="w-4 h-4 mr-2" />Sblocca</>}
          </Button>
        </div>
      </motion.div>

      {/* Lista ordini sbloccati */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600" />
          Ordini sbloccati
          {overrides.length > 0 && <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-0.5 rounded-full">{overrides.length}</span>}
        </h3>
        {renderList(overrides, 'override')}
      </motion.div>

      {/* ============ AZZERA COSTI DI GESTIONE ============ */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl border border-emerald-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BadgeEuro className="w-5 h-5 text-emerald-600" />
          Azzera costi di gestione
        </h3>
        <p className="text-xs text-gray-400 mb-3">Azzera i costi di gestione (cambio, reso, voucher) per un ordine specifico. Il cliente non pagherà alcun costo aggiuntivo.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input type="text" placeholder="Numero ordine (es: DForder_21938)" value={newWaiverOrder} onChange={(e) => setNewWaiverOrder(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddWaiver()} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="waiver-order-input" />
          </div>
          <div className="flex-1">
            <input type="text" placeholder="Motivo (opzionale)" value={newWaiverReason} onChange={(e) => setNewWaiverReason(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddWaiver()} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="waiver-reason-input" />
          </div>
          <Button onClick={handleAddWaiver} disabled={isAddingWaiver || !newWaiverOrder.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6" data-testid="waiver-add-btn">
            {isAddingWaiver ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BadgeEuro className="w-4 h-4 mr-2" />Azzera costi</>}
          </Button>
        </div>
      </motion.div>

      {/* Lista ordini con costi azzerati */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl border border-emerald-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BadgeEuro className="w-5 h-5 text-emerald-600" />
          Ordini con costi azzerati
          {waivers.length > 0 && <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">{waivers.length}</span>}
        </h3>
        {renderList(waivers, 'waiver')}
      </motion.div>
    </div>
  );
};

export default AdminEccezioniPage;
